import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Plus, Copy, Check, RefreshCw, Smartphone, Monitor, MonitorSmartphone, 
  Mail, Edit, Trash2, User, Users, Briefcase, MapPin, Star, AlertCircle,
  FileText, Clock, Shield, Eye, Building, Phone, Calendar
} from 'lucide-react';
import api from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role definitions with access information
const ROLE_DEFINITIONS = {
  customer: {
    name: 'Customer (Domestic)',
    description: 'Home moving customers',
    access: 'app_only',
    accessLabel: 'Mobile App Only',
    icon: User,
    color: 'bg-blue-100 text-blue-700',
    permissions: ['View own jobs', 'Create inventory', 'Chat with company', 'View estimates']
  },
  customer_commercial: {
    name: 'Customer (Commercial)',
    description: 'Business/office moving customers',
    access: 'app_only',
    accessLabel: 'Mobile App Only',
    icon: Building,
    color: 'bg-indigo-100 text-indigo-700',
    permissions: ['View own jobs', 'Create inventory', 'Chat with company', 'View estimates', 'Multiple locations']
  },
  porter: {
    name: 'Porter',
    description: 'Moving crew member',
    access: 'app_only',
    accessLabel: 'Mobile App Only',
    icon: Users,
    color: 'bg-green-100 text-green-700',
    permissions: ['View assigned jobs', 'Scan QR codes', 'Update job status', 'Clock in/out']
  },
  driver_crew: {
    name: 'Driver',
    description: 'Vehicle driver with crew',
    access: 'app_only',
    accessLabel: 'Mobile App Only',
    icon: Users,
    color: 'bg-emerald-100 text-emerald-700',
    permissions: ['View assigned jobs', 'GPS tracking', 'Update job status', 'Scan items', 'Clock in/out']
  },
  sales_rep: {
    name: 'Sales Representative',
    description: 'Sales and surveys',
    access: 'both',
    accessLabel: 'App + Panel',
    icon: Briefcase,
    color: 'bg-purple-100 text-purple-700',
    permissions: ['View leads', 'Create estimates', 'Conduct surveys', 'Access CRM', 'Mobile surveys']
  },
  customer_service: {
    name: 'Customer Service',
    description: 'Customer support staff',
    access: 'panel_only',
    accessLabel: 'Control Panel Only',
    icon: Phone,
    color: 'bg-pink-100 text-pink-700',
    permissions: ['View all jobs', 'Update job status', 'Chat with customers', 'View reports']
  },
  scheduler: {
    name: 'Scheduler',
    description: 'Job scheduling staff',
    access: 'panel_only',
    accessLabel: 'Control Panel Only',
    icon: Calendar,
    color: 'bg-orange-100 text-orange-700',
    permissions: ['View all jobs', 'Assign crews', 'Schedule jobs', 'Manage calendar']
  },
  operations_manager: {
    name: 'Operations Manager',
    description: 'Full operations access',
    access: 'both',
    accessLabel: 'App + Panel',
    icon: Shield,
    color: 'bg-red-100 text-red-700',
    permissions: ['Full job access', 'Manage staff', 'View reports', 'Approve estimates', 'Mobile oversight']
  },
  company_admin: {
    name: 'Company Admin',
    description: 'Full company access',
    access: 'panel_only',
    accessLabel: 'Control Panel Only',
    icon: Shield,
    color: 'bg-gray-800 text-white',
    permissions: ['Full access', 'Manage users', 'Billing', 'Settings', 'All reports']
  }
};

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showRoleGuide, setShowRoleGuide] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    role: 'customer',
    customer_type: 'domestic',
    access_type: 'app_only',
    send_credentials: true,
    // Customer address fields
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom'
  });
  
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    role: '',
    access_type: '',
    is_active: true,
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: ''
  });
  
  // Employee profile data
  const [profileData, setProfileData] = useState({
    employee_id: '',
    start_date: '',
    department: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    performance_rating: 0,
    conduct_notes: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/list');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    const roleInfo = ROLE_DEFINITIONS[role];
    setFormData({ 
      ...formData, 
      role,
      access_type: roleInfo?.access || 'both'
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('movemate_token');
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        email: formData.email,
        role: formData.role,
        access_type: formData.access_type,
        send_credentials: formData.send_credentials
      };
      
      // Add address for customers
      if (formData.role.includes('customer') && formData.address_line1) {
        payload.address = {
          line1: formData.address_line1,
          line2: formData.address_line2,
          city: formData.city,
          postcode: formData.postcode,
          country: formData.country
        };
      }
      
      const response = await fetch(`${API_URL}/api/users/create-with-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Failed to create user');
      
      setCreatedUser(data);
      toast.success('User created successfully!');
      fetchUsers();
      resetForm();
    } catch (error) {
      toast.error(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      role: 'customer',
      customer_type: 'domestic',
      access_type: 'app_only',
      send_credentials: true,
      address_line1: '',
      address_line2: '',
      city: '',
      postcode: '',
      country: 'United Kingdom'
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    setCopiedPassword(true);
    toast.success('Password copied to clipboard!');
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const regenerateCode = async (userId, sendEmail = true) => {
    try {
      const response = await api.post(`/users/${userId}/regenerate-code`, { send_email: sendEmail });
      toast.success(`New code: ${response.data.login_code}${sendEmail ? ' - Email sent!' : ''}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      email: user.email || '',
      role: user.role || '',
      access_type: user.access_type || 'both',
      is_active: user.is_active !== false,
      address_line1: user.address?.line1 || '',
      address_line2: user.address?.line2 || '',
      city: user.address?.city || '',
      postcode: user.address?.postcode || ''
    });
    setShowEditDialog(true);
  };

  const openProfileDialog = (user) => {
    setSelectedUser(user);
    setProfileData({
      employee_id: user.employee_id || '',
      start_date: user.start_date || '',
      department: user.department || '',
      emergency_contact: user.emergency_contact || '',
      emergency_phone: user.emergency_phone || '',
      notes: user.notes || '',
      performance_rating: user.performance_rating || 0,
      conduct_notes: user.conduct_notes || []
    });
    setShowProfileDialog(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...editFormData };
      if (editFormData.address_line1) {
        payload.address = {
          line1: editFormData.address_line1,
          line2: editFormData.address_line2,
          city: editFormData.city,
          postcode: editFormData.postcode
        };
      }
      await api.put(`/users/${selectedUser.id}`, payload);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/profile`, profileData);
      toast.success('Employee profile updated');
      setShowProfileDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const addConductNote = () => {
    const note = prompt('Enter conduct note:');
    if (note) {
      setProfileData({
        ...profileData,
        conduct_notes: [
          ...profileData.conduct_notes,
          { date: new Date().toISOString(), note, type: 'general' }
        ]
      });
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const getRoleInfo = (role) => ROLE_DEFINITIONS[role] || { name: role, color: 'bg-gray-100 text-gray-700', icon: User };

  const filteredUsers = users.filter(user => {
    if (activeTab === 'all') return true;
    if (activeTab === 'customers') return user.role?.includes('customer');
    if (activeTab === 'employees') return !user.role?.includes('customer');
    return true;
  });

  const currentRoleInfo = ROLE_DEFINITIONS[formData.role];

  return (
    <div className="space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">{t('users.title', 'User Management')}</h1>
          <p className="text-gray-600">{t('users.subtitle', 'Manage customers and employees')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowRoleGuide(true)}>
            <Eye size={18} className="mr-2" />
            {t('users.role_guide', 'View Roles Guide')}
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-btn">
                <Plus size={20} className="mr-2" />
                {t('users.add_user', 'Create User')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('users.add_user', 'Create New User')}</DialogTitle>
                <DialogDescription>
                  {t('users.create_description', 'Create a customer or employee account with appropriate access')}
                </DialogDescription>
              </DialogHeader>

              {createdUser ? (
                <div className="space-y-6">
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
                    <h3 className="text-2xl font-bold text-green-900 mb-2">{t('users.user_created', 'User Created!')}</h3>
                    <p className="text-green-700 mb-4">
                      {createdUser.user.first_name}&apos;s {t('users.account_ready', 'account is ready')}
                    </p>
                    
                    {createdUser.user.login_code && (
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-600 mb-2">{t('users.login_code', 'Mobile App Login Code')}:</p>
                        <p className="text-5xl font-bold text-primary tracking-widest mb-2">
                          {createdUser.user.login_code}
                        </p>
                        <Button onClick={() => copyCode(createdUser.user.login_code)} variant="outline" size="sm">
                          {copiedCode ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                          {copiedCode ? t('common.copied', 'Copied!') : t('common.copy', 'Copy Code')}
                        </Button>
                      </div>
                    )}
                    
                    {createdUser.user.temp_password && (
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-600 mb-2">{t('users.panel_password', 'Control Panel Password')}:</p>
                        <p className="text-xl font-mono font-bold text-gray-800 mb-2">
                          {createdUser.user.temp_password}
                        </p>
                        <Button onClick={() => copyPassword(createdUser.user.temp_password)} variant="outline" size="sm">
                          {copiedPassword ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                          {copiedPassword ? t('common.copied', 'Copied!') : t('common.copy', 'Copy Password')}
                        </Button>
                      </div>
                    )}
                  </div>

                  {formData.send_credentials && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-blue-700">
                        <Mail size={16} />
                        <span className="text-sm">Credentials sent to {createdUser.user.email}</span>
                      </div>
                    </div>
                  )}

                  <Button onClick={() => { setCreatedUser(null); setShowCreateDialog(false); }} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreateUser} className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Role Selection with Preview */}
                  <div>
                    <Label>Role *</Label>
                    <Select value={formData.role} onValueChange={handleRoleChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Customers</div>
                        <SelectItem value="customer">
                          <div className="flex items-center gap-2">
                            <User size={14} />
                            <span>Customer (Domestic)</span>
                            <Badge variant="outline" className="ml-2 text-xs">App Only</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="customer_commercial">
                          <div className="flex items-center gap-2">
                            <Building size={14} />
                            <span>Customer (Commercial)</span>
                            <Badge variant="outline" className="ml-2 text-xs">App Only</Badge>
                          </div>
                        </SelectItem>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">Field Staff</div>
                        <SelectItem value="porter">
                          <div className="flex items-center gap-2">
                            <Users size={14} />
                            <span>Porter</span>
                            <Badge variant="outline" className="ml-2 text-xs">App Only</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="driver_crew">
                          <div className="flex items-center gap-2">
                            <Users size={14} />
                            <span>Driver</span>
                            <Badge variant="outline" className="ml-2 text-xs">App Only</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="sales_rep">
                          <div className="flex items-center gap-2">
                            <Briefcase size={14} />
                            <span>Sales Rep</span>
                            <Badge variant="outline" className="ml-2 text-xs">App + Panel</Badge>
                          </div>
                        </SelectItem>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">Office Staff</div>
                        <SelectItem value="customer_service">
                          <div className="flex items-center gap-2">
                            <Phone size={14} />
                            <span>Customer Service</span>
                            <Badge variant="outline" className="ml-2 text-xs">Panel Only</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="scheduler">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} />
                            <span>Scheduler</span>
                            <Badge variant="outline" className="ml-2 text-xs">Panel Only</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="operations_manager">
                          <div className="flex items-center gap-2">
                            <Shield size={14} />
                            <span>Operations Manager</span>
                            <Badge variant="outline" className="ml-2 text-xs">App + Panel</Badge>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Role Info Box */}
                    {currentRoleInfo && (
                      <div className={`mt-3 p-3 rounded-lg ${currentRoleInfo.color}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {currentRoleInfo.icon && <currentRoleInfo.icon size={18} />}
                          <span className="font-semibold">{currentRoleInfo.name}</span>
                          <Badge variant="secondary" className="ml-auto">{currentRoleInfo.accessLabel}</Badge>
                        </div>
                        <p className="text-sm opacity-80 mb-2">{currentRoleInfo.description}</p>
                        <div className="text-xs">
                          <strong>Permissions:</strong> {currentRoleInfo.permissions?.join(' • ')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Access Type - Auto-set based on role but can override */}
                  <div>
                    <Label>Access Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, access_type: 'app_only' })}
                        disabled={currentRoleInfo?.access === 'panel_only'}
                        className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-all ${
                          formData.access_type === 'app_only' 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-gray-200 hover:border-gray-300'
                        } ${currentRoleInfo?.access === 'panel_only' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Smartphone size={20} />
                        <span className="text-xs font-medium">App Only</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, access_type: 'panel_only' })}
                        disabled={currentRoleInfo?.access === 'app_only'}
                        className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-all ${
                          formData.access_type === 'panel_only' 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-gray-200 hover:border-gray-300'
                        } ${currentRoleInfo?.access === 'app_only' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Monitor size={20} />
                        <span className="text-xs font-medium">Panel Only</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, access_type: 'both' })}
                        className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-all ${
                          formData.access_type === 'both' 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <MonitorSmartphone size={20} />
                        <span className="text-xs font-medium">Both</span>
                      </button>
                    </div>
                  </div>

                  {/* Customer Address Fields */}
                  {formData.role.includes('customer') && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <MapPin size={16} />
                        Customer Address (Optional)
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Add address if customer will not use the app to enter their own details
                      </p>
                      <div className="space-y-3">
                        <Input
                          placeholder="Address Line 1"
                          value={formData.address_line1}
                          onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                        />
                        <Input
                          placeholder="Address Line 2"
                          value={formData.address_line2}
                          onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="City"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          />
                          <Input
                            placeholder="Postcode"
                            value={formData.postcode}
                            onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="send_credentials"
                      checked={formData.send_credentials}
                      onChange={(e) => setFormData({ ...formData, send_credentials: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="send_credentials" className="text-sm cursor-pointer">
                      Send credentials via email
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs for filtering */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Users ({users.length})</TabsTrigger>
          <TabsTrigger value="customers">Customers ({users.filter(u => u.role?.includes('customer')).length})</TabsTrigger>
          <TabsTrigger value="employees">Employees ({users.filter(u => !u.role?.includes('customer')).length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredUsers.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    return (
                      <div
                        key={user.id}
                        className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                        data-testid={`user-${user.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-semibold text-lg">
                                {user.first_name} {user.last_name}
                              </h3>
                              <Badge className={roleInfo.color}>{roleInfo.name}</Badge>
                            </div>
                            {user.phone && <p className="text-sm text-gray-600">{user.phone}</p>}
                            {user.email && <p className="text-sm text-gray-500">{user.email}</p>}
                            {user.address && (
                              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <MapPin size={12} />
                                {user.address.city}, {user.address.postcode}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {user.login_code && (
                              <div className="text-right">
                                <p className="text-xs text-gray-500 mb-1">Login Code:</p>
                                <div className="flex items-center gap-1">
                                  <code className="text-xl font-bold text-primary">{user.login_code}</code>
                                  <Button size="sm" variant="ghost" onClick={() => copyCode(user.login_code)} title="Copy">
                                    <Copy size={14} />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => regenerateCode(user.id, true)} title="Regenerate & Email">
                                    <RefreshCw size={14} />
                                  </Button>
                                </div>
                              </div>
                            )}
                            <div className="flex flex-col items-end gap-1">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {user.is_active !== false ? 'Active' : 'Inactive'}
                              </span>
                              {user.access_type && (
                                <Badge variant="outline" className="text-xs">
                                  {user.access_type === 'app_only' && <><Smartphone size={10} className="mr-1" /> App</>}
                                  {user.access_type === 'panel_only' && <><Monitor size={10} className="mr-1" /> Panel</>}
                                  {user.access_type === 'both' && <><MonitorSmartphone size={10} className="mr-1" /> Both</>}
                                </Badge>
                              )}
                              <div className="flex gap-1 mt-2">
                                {!user.role?.includes('customer') && (
                                  <Button size="sm" variant="outline" onClick={() => openProfileDialog(user)} title="Employee Profile">
                                    <FileText size={14} />
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => openEditDialog(user)}>
                                  <Edit size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => openDeleteDialog(user)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Guide Dialog */}
      <Dialog open={showRoleGuide} onOpenChange={setShowRoleGuide}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>User Roles Guide</DialogTitle>
            <DialogDescription>Understanding access levels and permissions for each role</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="grid gap-4">
              {Object.entries(ROLE_DEFINITIONS).map(([key, role]) => (
                <Card key={key} className="overflow-hidden">
                  <div className={`p-4 ${role.color}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {role.icon && <role.icon size={24} />}
                        <div>
                          <h3 className="font-bold text-lg">{role.name}</h3>
                          <p className="text-sm opacity-80">{role.description}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-sm">{role.accessLabel}</Badge>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield size={14} className="text-gray-500" />
                      <span className="font-medium text-sm">Permissions:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {role.permissions?.map((perm, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{perm}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={editFormData.first_name}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={editFormData.last_name}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </div>
            
            {/* Address fields */}
            <div className="border rounded-lg p-3 space-y-2">
              <Label className="flex items-center gap-2"><MapPin size={14} /> Address</Label>
              <Input
                placeholder="Address Line 1"
                value={editFormData.address_line1}
                onChange={(e) => setEditFormData({ ...editFormData, address_line1: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="City"
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                />
                <Input
                  placeholder="Postcode"
                  value={editFormData.postcode}
                  onChange={(e) => setEditFormData({ ...editFormData, postcode: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={editFormData.is_active}
                onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="edit_is_active">Active</Label>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Employee Profile - {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID</Label>
                <Input
                  value={profileData.employee_id}
                  onChange={(e) => setProfileData({ ...profileData, employee_id: e.target.value })}
                  placeholder="EMP-001"
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={profileData.start_date}
                  onChange={(e) => setProfileData({ ...profileData, start_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Department</Label>
              <Input
                value={profileData.department}
                onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                placeholder="e.g., Operations, Sales"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Emergency Contact</Label>
                <Input
                  value={profileData.emergency_contact}
                  onChange={(e) => setProfileData({ ...profileData, emergency_contact: e.target.value })}
                />
              </div>
              <div>
                <Label>Emergency Phone</Label>
                <Input
                  value={profileData.emergency_phone}
                  onChange={(e) => setProfileData({ ...profileData, emergency_phone: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Performance Rating</Label>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setProfileData({ ...profileData, performance_rating: rating })}
                    className={`p-2 rounded ${
                      profileData.performance_rating >= rating ? 'text-yellow-500' : 'text-gray-300'
                    }`}
                  >
                    <Star size={24} fill={profileData.performance_rating >= rating ? 'currentColor' : 'none'} />
                  </button>
                ))}
                <span className="text-sm text-gray-500 ml-2">{profileData.performance_rating}/5</span>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Conduct Notes / History</Label>
                <Button size="sm" variant="outline" onClick={addConductNote}>
                  <Plus size={14} className="mr-1" /> Add Note
                </Button>
              </div>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                {profileData.conduct_notes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No conduct notes</p>
                ) : (
                  profileData.conduct_notes.map((note, idx) => (
                    <div key={idx} className="text-sm border-b pb-2">
                      <p className="text-xs text-gray-400">{new Date(note.date).toLocaleDateString()}</p>
                      <p>{note.note}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={profileData.notes}
                onChange={(e) => setProfileData({ ...profileData, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile}>Save Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.first_name} {selectedUser?.last_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
